import React, { useState, useEffect, useCallback } from 'react';
import { compsAPI } from '../api';
import AddressAutocomplete from './AddressAutocomplete';
import { toast } from 'sonner';
import { formatNumber, parseFormattedNumber, formatInputValue } from '../utils/formatNumber';
import { 
  ChevronDown, 
  ChevronUp, 
  Plus, 
  Trash2, 
  Edit3, 
  RefreshCw, 
  MapPin, 
  Home, 
  DollarSign, 
  Ruler,
  Calendar,
  AlertTriangle,
  CheckCircle2,
  Info,
  Loader2,
  X,
  RotateCcw,
  Building2,
  Lightbulb,
  Shield,
  TrendingUp,
  AlertCircle,
  Bed,
  Bath,
  Clock,
  Lock
} from 'lucide-react';

// G — one voice. The active ARV mode LIVES ON THE DOCUMENT (analysis.active_arv_mode) and
// the three mode results come with it (analysis.arv_by_mode). This component no longer
// owns a private cache that the mode buttons depend on: switching a mode asks the
// server to make that stored result the active one (no recomputation) and hands the
// FULL result to the page. Recalculating (after adding/removing a comp) is ONE call
// that computes the three modes and keeps the mode the user is on.
const _resultsFromByMode = (byMode, active) => (byMode && byMode.system && byMode.user && byMode.both
  ? { system: byMode.system, user: byMode.user, blended: byMode.both, selected: byMode[active] || byMode.both, mode: active }
  : null);

const ComparableManager = ({ analysisId, arvUsedComps = [], onARVUpdate, onCompsChanged, user = null, onLockedFeature = null, lang = 'en',
                             initialMode = 'both', arvByMode = null, outlierComps = [], sizeNoteComps = [] }) => {
  // F: the user's comps the engine flagged as not fitting the others — marked on their
  // card so the one to check is obvious from the list, not only from the alert above.
  const _outlierKey = (a) => String(a || '').split(',')[0].trim().toLowerCase();
  const _outlierSet = new Set((outlierComps || []).map(_outlierKey));
  const isOutlierComp = (c) => _outlierSet.has(_outlierKey(c.display_address || c.address));
  // F, option 2: a size note on the card — amber, informational, no penalty
  const sizeNoteFor = (c) => (sizeNoteComps || []).find(n => _outlierKey(n.address) === _outlierKey(c.display_address || c.address));
  // Plan gating: Check if user can add manual comps
  const canUseManualComps = user?.is_founder || user?.features?.manual_comps === true;
  
  // State
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [systemComps, setSystemComps] = useState([]);
  const [userComps, setUserComps] = useState([]);
  const [arvExplanation, setArvExplanation] = useState(null);
  const [subject, setSubject] = useState({});
  
  // UI state
  const [showSystemComps, setShowSystemComps] = useState(false);
  const [showUserComps, setShowUserComps] = useState(true);
  const [showAddComp, setShowAddComp] = useState(false);
  const [showARVExplanation, setShowARVExplanation] = useState(false);
  
  // Recalculation mode and results
  const [recalcMode, setRecalcMode] = useState(['system', 'user', 'both'].includes(initialMode) ? initialMode : 'both');
  const [recalcResults, setRecalcResults] = useState(() => _resultsFromByMode(arvByMode, initialMode));
  const [showRecalcResults, setShowRecalcResults] = useState(false);
  
  // Active ARV display data - derived from selected mode
  const [activeArvData, setActiveArvData] = useState(null);
  
  // Newly added comp - for summary card
  const [newlyAddedComp, setNewlyAddedComp] = useState(null);
  
  // Add comp form - with all fields
  const [newCompInput, setNewCompInput] = useState('');
  const [newCompPrice, setNewCompPrice] = useState('');
  const [newCompSqft, setNewCompSqft] = useState('');
  const [newCompBeds, setNewCompBeds] = useState('');
  const [newCompBaths, setNewCompBaths] = useState('');
  const [newCompSaleDate, setNewCompSaleDate] = useState('');
  const [newCompDistance, setNewCompDistance] = useState('');
  const [newCompPropertyType, setNewCompPropertyType] = useState('');
  const [newCompCondition, setNewCompCondition] = useState('');
  const [addingComp, setAddingComp] = useState(false);
  const [compLookupResult, setCompLookupResult] = useState(null);
  const [lookingUpComp, setLookingUpComp] = useState(false);
  const [enrichComp, setEnrichComp] = useState(false); // Enrichment toggle - OFF by default
  
  // Edit comp modal
  const [editingComp, setEditingComp] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Format input helper
  const handlePriceChange = (value, setter) => {
    const formatted = formatInputValue(value);
    setter(formatted);
  };

  // Fetch comps
  const fetchComps = useCallback(async () => {
    if (!analysisId) return;
    
    try {
      setLoading(true);
      const response = await compsAPI.getComps(analysisId);
      setSystemComps(response.data.system_comps || []);
      setUserComps(response.data.user_comps || []);
      setArvExplanation(response.data.arv_explanation || {});
      setSubject(response.data.subject || {});
    } catch (error) {
      console.error('Error fetching comps:', error);
      toast.error(lang === 'en' ? 'Failed to load comparables' : 'Error al cargar comparables');
    } finally {
      setLoading(false);
    }
  }, [analysisId, lang]);

  useEffect(() => {
    fetchComps();
  }, [fetchComps]);

  // The "System Comparables" pool is the RAW RentCast return (e.g. 40) including active
  // listings, distressed sales, and low-$/sqft RUINS the engine excludes. The user only
  // wants the QUALIFYING comps the ARV is actually built from — sold + similar/renovated +
  // non-distressed (a handful). Filter on ALL of:
  //   1. SOLD (not an active/pending listing),
  //   2. condition not 'distressed',
  //   3. quality score not 'Weak' (the engine's own similarity/quality judgment),
  //   4. NOT a $/sqft ruin — ppsf below half the median of the sold pool (catches the
  //      $14/sf, $40/sf rows vs a ~$130/sf renovated median; market-relative, not a fixed
  //      floor, so it works in any market).
  const _isSoldNotListing = (c) => {
    const t = String(c.comp_type || c.status || '').toLowerCase();
    const isListing = ['active', 'listing', 'pending', 'for sale', 'forsale', 'coming soon'].includes(t);
    return t === 'sold' || t === 'sale' || (!!c.sold_date && !isListing);
  };
  const _compPpsf = (c) => ((c.sale_price && c.sqft) ? c.sale_price / c.sqft : null);
  const _soldPpsfs = (systemComps || [])
    .filter(_isSoldNotListing).map(_compPpsf).filter((p) => p && p > 0).sort((a, b) => a - b);
  const _medianSoldPpsf = _soldPpsfs.length ? _soldPpsfs[Math.floor(_soldPpsfs.length / 2)] : 0;
  const isQualifyingSoldComp = (c) => {
    if (!_isSoldNotListing(c)) return false;
    const cond = String(c.condition_label || c.condition || '').toLowerCase();
    if (cond === 'distressed') return false;
    if (c.quality_score && c.quality_score.label === 'Weak') return false;
    const ppsf = _compPpsf(c);
    if (ppsf !== null && _medianSoldPpsf > 0 && ppsf < 0.5 * _medianSoldPpsf) return false; // ruin
    return true;
  };
  // PROBLEM 3/4: the DEFINITIVE qualifying set is the comps the ARV was ACTUALLY built
  // from (analysis.comps_used, passed as arvUsedComps — the handful, ~3-7). The heuristic
  // filter above is only a fallback when that isn't available. These are the ONLY comps
  // that render; everything else in the pool is not renderable anywhere on the page.
  const _mapUsedComp = (c) => ({
    ...c,
    display_address: c.display_address || c.address,
    sold_date: c.sold_date || c.sale_date,
    condition_label: c.condition_label || c.condition,
    sale_price: c.sale_price || c.price,
  });
  // G: arvUsedComps is now the ACTIVE mode's comps_used. Under the "System" heading only
  // the system-sourced ones belong; the user's own comps have their own list below.
  const _systemUsed = (Array.isArray(arvUsedComps) ? arvUsedComps : []).filter(c => (c.source || 'system') !== 'user');
  const _hasUsed = _systemUsed.length > 0;
  // BUG 1 LOCK: comps_used is AUTHORITATIVE. The heuristic fallback used to run
  // whenever comps_used was empty, and it judged "distressed" from
  // `condition_label` — a field the API often returns as null — so with missing
  // metadata it admitted the entire 38-comp pool inline. That is why the distressed
  // rows kept coming back after being "fixed". There is no longer a heuristic path:
  // if the engine did not tell us which comps it used, we render NONE, rather than guessing.
  const qualifyingSystemComps = _hasUsed ? _systemUsed.map(_mapUsedComp) : [];
  // How many the ARV left on the table. A COUNT ONLY — there is no list behind it. The
  // "show excluded" section used to render every comp handed to it and was the largest
  // distressed-comp surface in the app (the 82 rows). It has been deleted outright, so a
  // non-qualifying comp has nowhere to render even if one reached the client.
  const excludedCount = Math.max((systemComps || []).length - qualifyingSystemComps.length, 0);
  // Los comps del usuario que REALMENTE entran al ARV. El pool del backend filtra por
  // `include_in_arv: True` (compute_arv_unified), asi que el boton de modo no puede
  // anunciar `userComps.length`: con un comp desmarcado prometia uno mas de los que iba a
  // usar. Es la misma familia de los cuatro conteos contradictorios de la pantalla.
  const activeUserComps = (userComps || []).filter(c => c.include_in_arv !== false);
  // EL UMBRAL DE 3, DICHO ANTES DEL RECALCULO Y NO DESPUES.
  // El motor ya lo calcula y nadie lo lee: `check_data_coverage` marca LOW_COMP_COUNT y
  // valuation_confidence 'low' por debajo de 3 comps calificados. Aqui se publica como
  // FIRMEZA, nunca como permiso: el boton no se desactiva por tener 2, porque un comp que
  // el inversionista conoce de primera mano puede valer mas que tres que encontro el
  // sistema. Solo se dice lo que va a pasar, antes de que pase.
  const poolDelModo = (modo) => (
    modo === 'system' ? qualifyingSystemComps.length
      : modo === 'user' ? activeUserComps.length
      : qualifyingSystemComps.length + activeUserComps.length
  );

  // Helper to get diagnostic data for a specific mode
  const getModeData = useCallback((mode, results) => {
    if (!results) return null;
    
    const modeMap = {
      'system': results.system,
      'user': results.user,
      'both': results.blended
    };
    
    const data = modeMap[mode];
    if (!data) return null;
    
    // Build display object from the mode's arv_details
    const details = data.arv_details || {};
    const explainArv = details.explain_arv || {};
    
    // Get the appropriate values based on mode type
    let compsFound, compsFiltered, compsUsed, avgDistance, medianPpsf;
    
    if (mode === 'user') {
      // User Only mode - direct averaging, minimal filtering
      const activeUserComps = userComps.filter(c => c.include_in_arv !== false);
      compsFound = activeUserComps.length;
      compsFiltered = 0; // No filtering in user-only mode
      compsUsed = details.user_comp_count || activeUserComps.length;
      
      // Calculate avg distance from user comps
      const distances = activeUserComps.map(c => c.distance).filter(d => d !== null && d !== undefined);
      avgDistance = distances.length > 0 ? distances.reduce((a, b) => a + b, 0) / distances.length : 0;
      
      // Get PPSF from details or calculate
      medianPpsf = details.avg_user_ppsf || 0;
      if (!medianPpsf && activeUserComps.length > 0) {
        const ppsfs = activeUserComps.map(c => c.sqft > 0 ? c.sale_price / c.sqft : 0).filter(p => p > 0);
        medianPpsf = ppsfs.length > 0 ? ppsfs.reduce((a, b) => a + b, 0) / ppsfs.length : 0;
      }
    } else if (mode === 'both') {
      // Blended mode - use system values + blending info
      compsFound = explainArv.comps_found || details.comps_analyzed || details.total_comps_received || (systemComps.length + userComps.length);
      compsFiltered = explainArv.comps_rejected || details.comps_filtered || 0;
      compsUsed = explainArv.comps_used || details.total_usable_comps || 
                 ((details.system_comps_used || 0) + (details.user_comps_used || 0));
      avgDistance = explainArv.avg_distance || details.avg_distance || 0;
      medianPpsf = explainArv.median_ppsf || details.median_ppsf || details.avg_user_ppsf || 0;
    } else {
      // System mode - use V2 engine results
      compsFound = explainArv.comps_found || details.comps_analyzed || details.total_comps_received || systemComps.length;
      compsFiltered = explainArv.comps_rejected || details.comps_filtered || 0;
      compsUsed = explainArv.comps_used || details.total_usable_comps || details.system_comps_used || 0;
      avgDistance = explainArv.avg_distance || details.avg_distance || 0;
      medianPpsf = explainArv.median_ppsf || details.median_ppsf || 0;
    }
    
    return {
      arv: data.arv,
      arv_confidence: data.arv_confidence,
      arv_confidence_score: data.arv_confidence_score,
      comps_found: compsFound,
      comps_filtered: compsFiltered,
      comps_used: compsUsed,
      avg_distance: typeof avgDistance === 'number' ? avgDistance.toFixed(2) : 0,
      avg_ppsf: typeof medianPpsf === 'number' ? medianPpsf.toFixed(0) : 0,
      median_ppsf: typeof medianPpsf === 'number' ? medianPpsf.toFixed(0) : 0,
      selected_cluster: explainArv.selected_cluster || details.selected_cluster,
      renovated_count: explainArv.renovated_boosted ? 1 : (details.renovated_detected?.length || 0),
      distressed_count: explainArv.distressed_penalized ? 1 : (details.distressed_detected?.length || 0),
      data_quality_note: details.data_quality_note,
      user_weight: details.user_weight,
      blended_mode: details.blended_mode,
      mode: mode
    };
  }, [userComps, systemComps]);

  // Update activeArvData when mode changes or recalc results change
  useEffect(() => {
    if (recalcResults) {
      const modeData = getModeData(recalcMode, recalcResults);
      if (modeData) {
        setActiveArvData(modeData);
      }
    }
  }, [recalcMode, recalcResults, getModeData]);

  // Add user comp with enhanced data
  const handleAddComp = async () => {
    if (!newCompInput.trim()) {
      toast.error(lang === 'en' ? 'Please enter an address or URL' : 'Ingresa una dirección o URL');
      return;
    }
    
    // BUG 4 FIX: Validate sale price is required and > 0
    const salePrice = parseFormattedNumber(newCompPrice);
    if (!salePrice || salePrice <= 0) {
      toast.error(lang === 'en' ? 'Sale price is required' : 'El precio de venta es requerido');
      return;
    }
    
    setAddingComp(true);
    try {
      const response = await compsAPI.addComp({
        analysis_id: analysisId,
        input_value: newCompInput.trim(),
        sale_price: parseFormattedNumber(newCompPrice),
        sqft: newCompSqft ? parseInt(newCompSqft.replace(/,/g, '')) : null,
        beds: newCompBeds ? parseInt(newCompBeds) : null,
        baths: newCompBaths ? parseFloat(newCompBaths) : null,
        sold_date: newCompSaleDate || null,
        distance: newCompDistance ? parseFloat(newCompDistance) : null,
        property_type: newCompPropertyType || null,
        condition_label: newCompCondition || null,
        enrich: enrichComp // Pass enrichment flag - false by default
      });
      
      // Show newly added comp summary
      const addedComp = response.data.comp;
      setNewlyAddedComp(addedComp);
      
      // Show success message with enrichment info
      if (response.data.enriched) {
        toast.success(lang === 'en' ? 'Comparable added with market data!' : 'Comparable agregado con datos del mercado!');
      } else {
        toast.success(lang === 'en' ? 'Comparable added!' : 'Comparable agregado!');
      }
      
      // Reset form
      setNewCompInput('');
      setNewCompPrice('');
      setNewCompSqft('');
      setNewCompBeds('');
      setNewCompBaths('');
      setNewCompSaleDate('');
      setNewCompDistance('');
      setNewCompPropertyType('');
      setNewCompCondition('');
      setEnrichComp(false); // Reset enrichment toggle
      setShowAddComp(false);
      setCompLookupResult(null);
      
      fetchComps();
      
      // Notify parent that comps changed (for PDF warning)
      if (onCompsChanged) onCompsChanged();
      
      // Auto-trigger ARV recalculation after adding comp
      // Delay ensures fetchComps() completes and state updates before recalculation
      setTimeout(() => {
        toast.info(lang === 'en' 
          ? 'Recalculating ARV with new comparable...' 
          : 'Recalculando ARV con nuevo comparable...');
        handleRecalculateARV();
      }, 1500);
    } catch (error) {
      console.error('Error adding comp:', error);
      const detalle = error.response?.data?.detail;
      const errorMsg = (detalle && typeof detalle === 'object' ? detalle.message : detalle)
        || (lang === 'en' ? 'Failed to add comparable' : 'No se pudo agregar el comparable');
      toast.error(errorMsg);
    } finally {
      setAddingComp(false);
    }
  };

  // Delete user comp
  const handleDeleteComp = async (compId) => {
    if (!window.confirm(lang === 'en' ? 'Remove this comparable?' : '¿Eliminar este comparable?')) {
      return;
    }
    
    try {
      await compsAPI.deleteComp(compId);
      toast.success(lang === 'en' ? 'Comparable removed' : 'Comparable eliminado');
      setNewlyAddedComp(null);
      fetchComps();
      
      // Notify parent that comps changed (for PDF warning)
      if (onCompsChanged) onCompsChanged();
      
      // Auto-trigger ARV recalculation after removing comp
      // Delay ensures fetchComps() completes and state updates before recalculation
      setTimeout(() => {
        toast.info(lang === 'en' 
          ? 'Recalculating ARV after removing comparable...' 
          : 'Recalculando ARV después de eliminar comparable...');
        handleRecalculateARV();
      }, 1500);
    } catch (error) {
      console.error('Error deleting comp:', error);
      toast.error(lang === 'en' ? 'Failed to remove comparable' : 'Error al eliminar');
    }
  };

  // Update user comp
  const handleUpdateComp = async () => {
    if (!editingComp) return;
    
    try {
      await compsAPI.updateComp(editingComp.id, {
        sale_price: parseFormattedNumber(editForm.sale_price),
        sqft: editForm.sqft ? parseInt(String(editForm.sqft).replace(/,/g, '')) : null,
        beds: editForm.beds ? parseInt(editForm.beds) : null,
        baths: editForm.baths ? parseFloat(editForm.baths) : null,
        sold_date: editForm.sold_date || null,
        distance: editForm.distance ? parseFloat(editForm.distance) : null,
        condition_label: editForm.condition_label || null,
        include_in_arv: editForm.include_in_arv
      });
      
      toast.success(lang === 'en' ? 'Comparable updated - Quality score recalculated' : 'Comparable actualizado - Puntuación recalculada');
      setEditingComp(null);
      fetchComps();
    } catch (error) {
      console.error('Error updating comp:', error);
      toast.error(lang === 'en' ? 'Failed to update comparable' : 'Error al actualizar');
    }
  };

  // Recalculate ARV with all three modes
  const handleRecalculateARV = async () => {
    setRecalculating(true);
    setRecalcResults(null);
    
    try {
      // Get all three ARV values. ALWAYS call all three — the backend reads user
      // comps from the DB (compute_arv_unified), so a just-added comp is included
      // regardless of frontend state timing. This removes the stale-closure race:
      // previously these were gated on `userComps.length > 0` captured at add time
      // (0 for the first comp), so the blended/user calls were skipped and the
      // selected (Blended) result came back null -> top $0, User/Blended N/A.
      // 'user' mode returns UNABLE when the DB has no user comps; 'both' with no
      // user comps returns the system ARV — so nothing is ever nulled here.
      // G: ONE request computes the three modes and stores them together with the mode
      // the user is on; the three parallel calls of before each persisted their own
      // result, so the document kept whichever finished last.
      const oneCall = await compsAPI.recalculateARV({
        analysis_id: analysisId,
        active_arv_mode: recalcMode
      });
      const byMode = oneCall.data?.arv_by_mode || {};
      const selectedResult = oneCall.data;

      // Store all results for display
      const results = {
        system: byMode.system,
        user: byMode.user,
        blended: byMode.both,
        selected: selectedResult,
        mode: recalcMode
      };
      setRecalcResults(results);
      setShowRecalcResults(true);
      
      // Immediately set active ARV data for the selected mode
      const modeData = getModeData(recalcMode, results);
      if (modeData) {
        setActiveArvData(modeData);
      }
      
      toast.success(lang === 'en' 
        ? `ARV Updated: $${selectedResult.arv?.toLocaleString()}` 
        : `ARV Actualizado: $${selectedResult.arv?.toLocaleString()}`);
      
      // Refresh comps to get updated ARV explanation
      fetchComps();
      
      // Notify parent of ARV update with mode label AND active comps
      if (onARVUpdate) {
        const modeLabelMap = {
          'system': lang === 'en' ? 'System Only' : 'Solo Sistema',
          'user': lang === 'en' ? 'User Only' : 'Solo Usuario',
          'both': lang === 'en' ? 'Blended' : 'Combinado'
        };
        
        // Determine which comps are active based on mode
        let activeComps = [];
        if (recalcMode === 'system') {
          activeComps = systemComps;
        } else if (recalcMode === 'user') {
          activeComps = userComps.filter(c => c.include_in_arv !== false);
        } else {
          // Blended: include both system and active user comps
          activeComps = [...systemComps, ...userComps.filter(c => c.include_in_arv !== false)];
        }
        
        const resultWithMode = {
          ...selectedResult,
          arv_details: {
            ...(selectedResult.arv_details || {}),
            mode_label: modeLabelMap[recalcMode],
            active_mode: recalcMode
          },
          // Include active comps for PDF/export
          active_comps: activeComps,
          active_arv_mode: recalcMode
        };
        onARVUpdate(resultWithMode);
      }
    } catch (error) {
      console.error('Error recalculating ARV:', error);
      toast.error(lang === 'en' ? 'Failed to recalculate ARV' : 'Error al recalcular ARV');
    } finally {
      setRecalculating(false);
    }
  };

  // G: switching the mode asks the server to make the STORED result of that mode the
  // active one — no recomputation, no dependence on a page-session cache — and hands
  // the FULL result to the page. Works right after a reload, and before any
  // "Recalculate" click.
  const handleModeSwitch = useCallback(async (newMode) => {
    setRecalcMode(newMode);
    if (!analysisId) return;
    let modeResult = null;
    try {
      const res = await compsAPI.setARVMode(analysisId, newMode);
      modeResult = res.data;
    } catch (error) {
      console.error('Error switching ARV mode:', error);
      toast.error(lang === 'en' ? 'Could not switch the ARV mode' : 'No se pudo cambiar el modo de ARV');
      return;
    }
    const byMode = modeResult?.arv_by_mode;
    const results = byMode
      ? { system: byMode.system, user: byMode.user, blended: byMode.both, selected: modeResult, mode: newMode }
      : null;
    if (results) {
      setRecalcResults(results);
      const modeData = getModeData(newMode, results);
      if (modeData) setActiveArvData(modeData);
    }
    if (!onARVUpdate) return;
    const modeLabelMap = {
      'system': lang === 'en' ? 'System Only' : 'Solo Sistema',
      'user': lang === 'en' ? 'User Only' : 'Solo Usuario',
      'both': lang === 'en' ? 'Blended' : 'Combinado'
    };
    let activeComps = [];
    if (newMode === 'system') {
      activeComps = systemComps;
    } else if (newMode === 'user') {
      activeComps = userComps.filter(c => c.include_in_arv !== false);
    } else {
      activeComps = [...systemComps, ...userComps.filter(c => c.include_in_arv !== false)];
    }
    onARVUpdate({
      ...modeResult,
      arv_details: {
        ...(modeResult.arv_details || {}),
        mode_label: modeLabelMap[newMode],
        active_mode: newMode
      },
      active_comps: activeComps,
      active_arv_mode: newMode
    });
  }, [analysisId, getModeData, onARVUpdate, lang, systemComps, userComps]);

  // Check for missing fields
  const getMissingFields = (comp) => {
    const missing = [];
    if (!comp.sale_price) missing.push('sale_price');
    if (!comp.sqft) missing.push('sqft');
    if (!comp.sold_date) missing.push('sale_date');
    if (!comp.distance && comp.distance !== 0) missing.push('distance');
    if (!comp.beds) missing.push('beds');
    if (!comp.baths) missing.push('baths');
    return missing;
  };

  // What a partially-measured quality score says, and why it is worded this way.
  //
  // APPROVED COPY (comp square footage case):
  //   EN  Partial quality score — no square footage for this comparable.
  //   ES  Puntuación de calidad parcial — este comparable no trae metros cuadrados.
  //
  // `unmeasured` can name three different gaps, so the other two follow the identical
  // structure — "Partial quality score —" then the thing that is missing. Those two
  // variants are NOT the approved wording; they mirror it so a reader never sees an
  // internal key like `market_median_ppsf`.
  const PARTIAL_REASONS = {
    comp_sqft: {
      en: 'no square footage for this comparable.',
      es: 'este comparable no trae metros cuadrados.',
    },
    subject_sqft: {
      en: 'no square footage for this property.',
      es: 'esta propiedad no trae metros cuadrados.',
    },
    market_median_ppsf: {
      en: 'no market $/sqft to compare against.',
      es: 'no hay $/m² de mercado para comparar.',
    },
  };

  const partialNote = (unmeasured, lang) => {
    const isEn = lang === 'en';
    const head = isEn ? 'Partial quality score' : 'Puntuación de calidad parcial';
    const parts = (unmeasured || [])
      .map((k) => PARTIAL_REASONS[k] && (isEn ? PARTIAL_REASONS[k].en : PARTIAL_REASONS[k].es))
      .filter(Boolean);
    if (!parts.length) return `${head}.`;
    return `${head} — ${parts.join(' ')}`;
  };

  // ONE definition of "how confident is this value", shared by every badge below.
  //
  // The badge used to render the backend's `arv_confidence` LABEL, whose threshold for
  // "high" sits near 80, while the alarm on the results screen fires below 88. So a
  // property scoring 82 got a green "Confidence: High" chip a few centimetres from
  // "Weak comparable support". Two components answering the same question with different
  // thresholds is not a copy problem; it is two sources of truth.
  //
  // Now both read the SCORE, at the alarm's threshold. And the top band deliberately does
  // not say "High": the validated half of the measurement says a score below 88 means
  // unreliable, and says nothing about a high score meaning reliable — it failed on the
  // first new property it cleared. So the badge withholds alarm, it never grants
  // reassurance. See BACKLOG.md, "Locked-rule validation".
  const ARV_CONFIDENCE_ALARM_THRESHOLD = 88;

  const confidenceChip = (score, lang) => {
    const n = Number(score);
    // An ABSENT score is not evidence of a good one.
    if (!Number.isFinite(n)) {
      return { text: lang === 'en' ? 'Not scored' : 'Sin puntuar',
               cls: 'bg-slate-100 text-slate-600' };
    }
    if (n < ARV_CONFIDENCE_ALARM_THRESHOLD) {
      return { text: lang === 'en' ? 'Limited' : 'Limitada',
               cls: 'bg-amber-100 text-amber-800' };
    }
    return { text: lang === 'en' ? 'Comps agree' : 'Comps coinciden',
             cls: 'bg-slate-100 text-slate-700' };
  };

  // Quality score badge component
  const QualityBadge = ({ score, showDetails = false }) => {
    if (!score) return null;
    
    const { label, overall_score, reasons } = score;
    // A score computed on only part of the factors must not read like a complete one.
    // `scored_on_weight` is the share of the weighting that could actually be measured;
    // `unmeasured` names what was skipped (a missing subject size, a comp with no size,
    // no market median). Those used to be filled with 1,500 / 1 / $100 and the label came
    // back looking fully informed. See BACKLOG.md, "METHOD RULE".
    const scoredOnWeight = typeof score.scored_on_weight === 'number' ? score.scored_on_weight : 1;
    const isPartial = scoredOnWeight < 0.999;
    const unmeasured = Array.isArray(score.unmeasured) ? score.unmeasured : [];
    const colorMap = {
      'Strong': { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200', icon: '✓' },
      'Good': { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200', icon: '○' },
      'Fair': { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-200', icon: '△' },
      'Weak': { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200', icon: '✗' }
    };
    
    const colors = colorMap[label] || colorMap['Fair'];
    
    return (
      <div className="group relative">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${colors.bg} ${colors.text} ${colors.border}`}>
          <Shield className="w-3 h-3" />
          {label} ({overall_score})
          {isPartial && (
            <span
              className="ml-0.5 font-normal opacity-80"
              data-testid="quality-partial-marker"
              // The exact share stays internal: a DOM attribute for debugging and tests,
              // never part of the sentence the user reads.
              data-scored-on-weight={scoredOnWeight}
              title={partialNote(unmeasured, lang)}
            >
              {lang === 'en' ? '· partial' : '· parcial'}
            </span>
          )}
        </span>
        {/* Tooltip with reasons */}
        {reasons && reasons.length > 0 && (
          <div className="absolute z-50 bottom-full left-0 mb-2 w-64 p-3 bg-slate-800 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 shadow-lg">
            <div className="font-semibold mb-2">{lang === 'en' ? 'Quality Factors:' : 'Factores de Calidad:'}</div>
            <ul className="space-y-1">
              {reasons.map((reason, idx) => (
                <li key={idx} className="flex items-center gap-1.5">
                  {reason.includes('close') || reason.includes('similar') || reason.includes('recent') ? (
                    <CheckCircle2 className="w-3 h-3 text-green-400 flex-shrink-0" />
                  ) : reason.includes('diff') || reason.includes('far') || reason.includes('old') ? (
                    <AlertCircle className="w-3 h-3 text-amber-400 flex-shrink-0" />
                  ) : (
                    <Info className="w-3 h-3 text-blue-400 flex-shrink-0" />
                  )}
                  <span>{reason}</span>
                </li>
              ))}
            </ul>
            <div className="absolute top-full left-4 border-4 border-transparent border-t-slate-800"></div>
          </div>
        )}
      </div>
    );
  };

  // Newly Added Comp Summary Card
  const NewlyAddedCompCard = ({ comp, onDismiss }) => {
    const quality = comp.quality_score;
    const qualityLabel = quality?.label || 'Fair';
    const colorMap = {
      'Strong': { bg: 'bg-green-50', border: 'border-green-300', text: 'text-green-800' },
      'Good': { bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-800' },
      'Fair': { bg: 'bg-yellow-50', border: 'border-yellow-300', text: 'text-yellow-800' },
      'Weak': { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-800' }
    };
    const colors = colorMap[qualityLabel] || colorMap['Fair'];
    
    return (
      <div className={`p-4 rounded-xl border-2 ${colors.bg} ${colors.border} mb-4 animate-fade-in`} data-testid="newly-added-comp">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <CheckCircle2 className={`w-5 h-5 ${colors.text}`} />
            <span className={`font-semibold ${colors.text}`}>
              {lang === 'en' ? 'Comparable Added' : 'Comparable Agregado'}
            </span>
          </div>
          <button onClick={onDismiss} className="text-slate-400 hover:text-slate-600">
            <X className="w-4 h-4" />
          </button>
        </div>
        
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-slate-500" />
            <span className="font-medium">{comp.address}</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2">
            <div>
              <span className="text-slate-500 text-xs">{lang === 'en' ? 'Sale Price' : 'Precio'}</span>
              <div className="font-semibold">${comp.sale_price?.toLocaleString()}</div>
            </div>
            <div>
              <span className="text-slate-500 text-xs">{lang === 'en' ? 'Sqft' : 'Pies²'}</span>
              <div className="font-semibold">{comp.sqft?.toLocaleString() || 'N/A'}</div>
            </div>
            <div>
              <span className="text-slate-500 text-xs">{lang === 'en' ? 'Distance' : 'Distancia'}</span>
              <div className="font-semibold">{comp.distance ? `${comp.distance.toFixed(2)} mi` : 'N/A'}</div>
            </div>
            <div>
              <span className="text-slate-500 text-xs">{lang === 'en' ? 'Quality' : 'Calidad'}</span>
              <div className="font-semibold">{qualityLabel.toUpperCase()}</div>
            </div>
          </div>
          
          {/* Quality reasons */}
          {quality?.reasons && quality.reasons.length > 0 && (
            <div className={`mt-3 pt-3 border-t ${colors.border}`}>
              <div className={`text-xs font-medium ${colors.text} mb-1`}>
                {lang === 'en' ? 'Quality Factors:' : 'Factores:'}
              </div>
              <div className="flex flex-wrap gap-1">
                {quality.reasons.map((reason, idx) => (
                  <span key={idx} className={`px-2 py-0.5 rounded-full text-xs ${colors.bg} ${colors.text}`}>
                    • {reason}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Missing fields warning */}
          {getMissingFields(comp).length > 0 && (
            <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-amber-700">
                <span className="font-medium">
                  {lang === 'en' ? 'Some fields are missing.' : 'Faltan algunos campos.'}
                </span>{' '}
                {lang === 'en' 
                  ? 'Enter manually to improve comp quality.'
                  : 'Ingresa manualmente para mejorar la calidad.'}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Recalculation Results Panel
  const RecalcResultsPanel = () => {
    if (!recalcResults) return null;
    
    const { system, user, blended } = recalcResults;
    
    // Use recalcMode (which updates immediately) instead of mode from results
    const activeMode = recalcMode;
    const activeResult = activeMode === 'system' ? system : activeMode === 'user' ? user : blended;
    
    return (
      <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl mb-4 animate-fade-in" data-testid="recalc-results">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            <span className="font-semibold text-green-800">
              {lang === 'en' ? 'ARV Updated - Click to switch mode' : 'ARV Actualizado - Clic para cambiar modo'}
            </span>
          </div>
          <button onClick={() => setShowRecalcResults(false)} className="text-slate-400 hover:text-slate-600">
            <X className="w-4 h-4" />
          </button>
        </div>
        
        <div className="grid grid-cols-3 gap-3 mb-3">
          <button
            onClick={() => handleModeSwitch('system')}
            className={`p-3 rounded-lg text-center transition-all ${
              activeMode === 'system' 
                ? 'bg-blue-100 border-2 border-blue-400 ring-2 ring-blue-200' 
                : 'bg-white/70 hover:bg-white hover:border-blue-200 border-2 border-transparent'
            }`}
          >
            <div className="text-lg font-bold text-slate-800">
              ${system?.arv?.toLocaleString() || 'N/A'}
            </div>
            <div className="text-xs text-slate-600">
              {lang === 'en' ? 'System Only' : 'Solo Sistema'}
            </div>
          </button>
          <button
            onClick={() => handleModeSwitch('user')}
            disabled={!user?.arv}
            data-testid="mode-card-user"
            className={`p-3 rounded-lg text-center transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
              activeMode === 'user' 
                ? 'bg-blue-100 border-2 border-blue-400 ring-2 ring-blue-200' 
                : 'bg-white/70 hover:bg-white hover:border-blue-200 border-2 border-transparent'
            }`}
          >
            <div className="text-lg font-bold text-slate-800">
              ${user?.arv?.toLocaleString() || 'N/A'}
            </div>
            <div className="text-xs text-slate-600">
              {lang === 'en' ? 'User Only' : 'Solo Usuario'}
            </div>
          </button>
          <button
            onClick={() => handleModeSwitch('both')}
            disabled={!blended?.arv}
            data-testid="mode-card-both"
            className={`p-3 rounded-lg text-center transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
              activeMode === 'both' 
                ? 'bg-blue-100 border-2 border-blue-400 ring-2 ring-blue-200' 
                : 'bg-white/70 hover:bg-white hover:border-blue-200 border-2 border-transparent'
            }`}
          >
            <div className="text-lg font-bold text-slate-800">
              ${blended?.arv?.toLocaleString() || 'N/A'}
            </div>
            <div className="text-xs text-slate-600">
              {lang === 'en' ? 'Blended' : 'Combinado'}
            </div>
          </button>
        </div>
        
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <span className="text-slate-600">{lang === 'en' ? 'Confidence:' : 'Confianza:'}</span>
            {(() => {
              const chip = confidenceChip(activeResult?.arv_confidence_score, lang);
              return (
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${chip.cls}`}
                      data-testid="arv-confidence-chip">
                  {chip.text}
                </span>
              );
            })()}
          </div>
          {activeMode === 'both' && blended?.arv_details?.user_weight && (
            <div className="text-xs text-slate-500">
              {lang === 'en' ? 'User weight:' : 'Peso usuario:'} {Math.round(blended.arv_details.user_weight * 100)}%
            </div>
          )}
        </div>
      </div>
    );
  };

  // Comp card component
  const CompCard = ({ comp, isUser = false, onEdit, onDelete }) => {
    const missingFields = getMissingFields(comp);
    const hasMissingFields = missingFields.length > 0;
    
    return (
      <div 
        className={`p-3 rounded-lg border transition-all ${
          comp.include_in_arv === false 
            ? 'bg-gray-50 border-gray-200 opacity-60' 
            : hasMissingFields && isUser
              ? 'bg-amber-50 border-amber-200'
              : 'bg-white border-slate-200 hover:border-blue-300'
        }`}
        data-testid={`comp-card-${isUser ? 'user' : 'system'}`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <MapPin className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
              <span className="font-medium text-sm text-slate-800 truncate">
                {comp.display_address || comp.address || 'Unknown Address'}
              </span>
            </div>
            
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 mb-2">
              <span className="flex items-center gap-1">
                <DollarSign className="w-3 h-3" />
                ${formatNumber(comp.sale_price)}
              </span>
              {comp.sqft && (
                <span className="flex items-center gap-1">
                  <Ruler className="w-3 h-3" />
                  {formatNumber(comp.sqft)} sqft
                </span>
              )}
              {(comp.distance || comp.distance === 0) && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {comp.distance.toFixed(2)} mi
                </span>
              )}
              {comp.beds && comp.baths && (
                <span className="flex items-center gap-1">
                  <Bed className="w-3 h-3" />
                  {comp.beds}bd/{comp.baths}ba
                </span>
              )}
              {comp.sold_date && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {new Date(comp.sold_date).toLocaleDateString()}
                </span>
              )}
            </div>
            
            <div className="flex flex-wrap items-center gap-2">
              <QualityBadge score={comp.quality_score} />
              {isUser && isOutlierComp(comp) && (
                <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-600 text-white" data-testid="comp-outlier-badge">
                  {lang === 'en' ? '⚠ Check this comp' : '⚠ Revisar este comp'}
                </span>
              )}
              {isUser && !isOutlierComp(comp) && sizeNoteFor(comp) && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-300" data-testid="comp-size-note-badge">
                  {lang === 'en'
                    ? `${sizeNoteFor(comp).pct}% ${sizeNoteFor(comp).direction === 'above' ? 'bigger' : 'smaller'} — size-adjusted`
                    : `${sizeNoteFor(comp).pct}% ${sizeNoteFor(comp).direction === 'above' ? 'más grande' : 'más pequeño'} — ajustado por tamaño`}
                </span>
              )}
              
              {comp.condition_label && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  comp.condition_label === 'renovated' ? 'bg-green-100 text-green-700' :
                  comp.condition_label === 'distressed' ? 'bg-red-100 text-red-700' :
                  'bg-slate-100 text-slate-600'
                }`}>
                  {comp.condition_label}
                </span>
              )}
              
              {isUser && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                  User Added
                </span>
              )}
              
              {comp.include_in_arv === false && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-600">
                  Excluded
                </span>
              )}
              
              {hasMissingFields && isUser && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                  {missingFields.length} {lang === 'en' ? 'fields missing' : 'campos faltantes'}
                </span>
              )}
            </div>
          </div>
          
          {isUser && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => onEdit(comp)}
                className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                title={lang === 'en' ? 'Edit' : 'Editar'}
              >
                <Edit3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => onDelete(comp.id)}
                className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                title={lang === 'en' ? 'Remove' : 'Eliminar'}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
          <span className="ml-2 text-slate-500">
            {lang === 'en' ? 'Loading comparables...' : 'Cargando comparables...'}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="comparable-manager">
      {/* Header */}
      <div className="p-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              {lang === 'en' ? 'Comparable Sales Override' : 'Ventas Comparables'}
            </h2>
            <p className="text-sm text-slate-500 mt-0.5">
              {lang === 'en' 
                ? 'Manage comparables used for ARV calculation' 
                : 'Gestiona los comparables usados para el cálculo de ARV'}
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowARVExplanation(!showARVExplanation)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                showARVExplanation 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
              }`}
              data-testid="explain-arv-btn"
            >
              <Lightbulb className="w-4 h-4" />
              {lang === 'en' ? 'Explain ARV' : 'Explicar ARV'}
            </button>
          </div>
        </div>
      </div>

      {/* ARV Explanation Panel */}
      {showARVExplanation && (arvExplanation || activeArvData) && (() => {
        // Use activeArvData when available (after mode switch), otherwise use arvExplanation
        const displayData = activeArvData || arvExplanation;
        const modeLabel = activeArvData?.mode === 'system' 
          ? (lang === 'en' ? 'System Only' : 'Solo Sistema')
          : activeArvData?.mode === 'user' 
            ? (lang === 'en' ? 'User Only' : 'Solo Usuario')
            : activeArvData?.mode === 'both'
              ? (lang === 'en' ? 'Blended' : 'Combinado')
              : null;
        
        return (
        <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border-b border-blue-200" data-testid="arv-explanation-panel">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-blue-800 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              {lang === 'en' ? 'ARV Calculation Summary' : 'Resumen del Cálculo de ARV'}
            </h3>
            {modeLabel && (
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-600 text-white">
                {modeLabel}
              </span>
            )}
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-3">
            <div className="bg-white/80 rounded-lg p-2.5 text-center">
              <div className="text-lg font-bold text-blue-600">${formatNumber(displayData.arv)}</div>
              <div className="text-xs text-blue-700">
                {lang === 'en' ? 'Estimated ARV' : 'ARV Estimado'}
              </div>
            </div>
            <div className="bg-white/80 rounded-lg p-2.5 text-center">
              {/* BUG 2: show usable SOLD comps, never the raw pool (40) with listings/distressed.
                  Use the qualifying count directly — the backend comps_found can be the raw len. */}
              <div className="text-lg font-bold text-blue-600">{qualifyingSystemComps.length || 0}</div>
              <div className="text-xs text-blue-700">
                {lang === 'en' ? 'Sold Comps' : 'Comps Vendidos'}
              </div>
            </div>
            <div className="bg-white/80 rounded-lg p-2.5 text-center">
              <div className="text-lg font-bold text-blue-600">{displayData.comps_filtered || 0}</div>
              <div className="text-xs text-blue-700">
                {lang === 'en' ? 'Filtered Out' : 'Filtrados'}
              </div>
            </div>
            <div className="bg-white/80 rounded-lg p-2.5 text-center">
              {/* THE LIST WINS. `displayData.comps_used` is a NUMBER from the backend
                  (server.py: len(final_comps)); `qualifyingSystemComps` is the LIST the
                  table renders. They are different fields with the same name, and on
                  5060 Tireman the number said 4 while the list was empty — "4 Used in
                  ARV" printed above a table with no rows.
                  Until the response contract is unified (its own cycle), the tile shows
                  what the user can actually see. A count nobody can check is worse than
                  a smaller one they can. */}
              <div className="text-lg font-bold text-blue-600" data-testid="comps-used-tile">
                {qualifyingSystemComps.length}
              </div>
              <div className="text-xs text-blue-700">
                {lang === 'en' ? 'Used in ARV' : 'Usados en ARV'}
              </div>
            </div>
            <div className="bg-white/80 rounded-lg p-2.5 text-center">
              <div className="text-lg font-bold text-blue-600">{displayData.avg_distance || 0} mi</div>
              <div className="text-xs text-blue-700">
                {lang === 'en' ? 'Avg Distance' : 'Dist. Prom.'}
              </div>
            </div>
            <div className="bg-white/80 rounded-lg p-2.5 text-center">
              <div className="text-lg font-bold text-blue-600">${displayData.avg_ppsf || displayData.median_ppsf || 0}</div>
              <div className="text-xs text-blue-700">
                {lang === 'en' ? 'Median $/Sqft' : 'Med. $/Pie²'}
              </div>
            </div>
          </div>
          
          {/* Mode-specific explanation */}
          {displayData.comps_used > 0 && (
            <div className="mb-3 p-2 bg-blue-100/50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-700">
                <Info className="w-3.5 h-3.5 inline mr-1" />
                {activeArvData?.mode === 'user' ? (
                  lang === 'en' 
                    ? `${displayData.comps_used} user-added comparable${displayData.comps_used > 1 ? 's' : ''} used in direct ARV calculation.`
                    : `${displayData.comps_used} comparable${displayData.comps_used > 1 ? 's' : ''} agregados por usuario usados en cálculo directo de ARV.`
                ) : activeArvData?.mode === 'both' ? (
                  lang === 'en' 
                    ? `Blended ARV using system and user comparables.${displayData.user_weight ? ` User weight: ${Math.round(displayData.user_weight * 100)}%.` : ''}`
                    : `ARV combinado usando comparables del sistema y usuario.${displayData.user_weight ? ` Peso usuario: ${Math.round(displayData.user_weight * 100)}%.` : ''}`
                ) : (
                  <>
                    {lang === 'en' 
                      ? `Top ${displayData.comps_used} comparable${displayData.comps_used > 1 ? 's' : ''} used in ARV calculation${displayData.comps_filtered > 0 ? ' after quality filtering' : ''}.`
                      : `Los ${displayData.comps_used} mejores comparable${displayData.comps_used > 1 ? 's' : ''} usados en el cálculo de ARV${displayData.comps_filtered > 0 ? ' después del filtrado de calidad' : ''}.`}
                    {displayData.selected_cluster && (
                      <span className="ml-1">
                        {lang === 'en' 
                          ? `Selected cluster: ${displayData.selected_cluster}.`
                          : `Grupo seleccionado: ${displayData.selected_cluster}.`}
                      </span>
                    )}
                  </>
                )}
              </p>
            </div>
          )}
          
          <div className="flex flex-wrap gap-2 text-xs">
            {displayData.renovated_count > 0 && (
              <span className="px-2 py-1 rounded-full bg-green-100 text-green-700">
                {displayData.renovated_count} {lang === 'en' ? 'renovated comps boosted' : 'comps renovados impulsados'}
              </span>
            )}
            {displayData.distressed_count > 0 && (
              <span className="px-2 py-1 rounded-full bg-amber-100 text-amber-700">
                {displayData.distressed_count} {lang === 'en' ? 'distressed comps penalized' : 'comps en dificultades penalizados'}
              </span>
            )}
            {displayData.selected_cluster && (
              <span className="px-2 py-1 rounded-full bg-purple-100 text-purple-700">
                {lang === 'en' ? 'Cluster:' : 'Grupo:'} {displayData.selected_cluster}
              </span>
            )}
            {displayData.user_weight && (
              <span className="px-2 py-1 rounded-full bg-indigo-100 text-indigo-700">
                {lang === 'en' ? 'User Weight:' : 'Peso Usuario:'} {Math.round(displayData.user_weight * 100)}%
              </span>
            )}
            {(() => {
              const chip = confidenceChip(displayData.arv_confidence_score, lang);
              return (
                <span className={`px-2 py-1 rounded-full ${chip.cls}`}
                      data-testid="arv-confidence-chip-summary">
                  {lang === 'en' ? 'Confidence:' : 'Confianza:'} {chip.text}
                </span>
              );
            })()}
          </div>
          
          {displayData.data_quality_note && (
            <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700">{displayData.data_quality_note}</p>
            </div>
          )}
        </div>
        );
      })()}

      {/* Recalculation Results */}
      {showRecalcResults && <RecalcResultsPanel />}

      {/* Recalculation Controls */}
      <div className="p-4 bg-slate-50 border-b border-slate-200">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-600">
              {lang === 'en' ? 'ARV Mode:' : 'Modo ARV:'}
            </span>
            <div className="flex rounded-lg border border-slate-300 overflow-hidden bg-white">
              <button
                onClick={() => handleModeSwitch('system')}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  recalcMode === 'system' 
                    ? 'bg-blue-600 text-white' 
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
                data-testid="mode-system"
              >
                {lang === 'en' ? 'System' : 'Sistema'} ({qualifyingSystemComps.length})
              </button>
              <button
                onClick={() => handleModeSwitch('user')}
                disabled={activeUserComps.length === 0}
                className={`px-3 py-1.5 text-sm font-medium border-x border-slate-300 transition-colors disabled:opacity-50 ${
                  recalcMode === 'user' 
                    ? 'bg-blue-600 text-white' 
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
                data-testid="mode-user"
              >
                {lang === 'en' ? 'User Only' : 'Usuario'} ({activeUserComps.length})
              </button>
              <button
                onClick={() => handleModeSwitch('both')}
                disabled={activeUserComps.length === 0}
                className={`px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50 ${
                  recalcMode === 'both' 
                    ? 'bg-blue-600 text-white' 
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
                data-testid="mode-blended"
              >
                {lang === 'en' ? 'Blended' : 'Combinado'} ({poolDelModo('both')})
              </button>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setRecalcMode('both'); setShowRecalcResults(false); setActiveArvData(null); setRecalcResults(null); fetchComps(); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-200 rounded-lg transition-colors"
              title={lang === 'en' ? 'Reset to defaults' : 'Restablecer valores'}
            >
              <RotateCcw className="w-4 h-4" />
              {lang === 'en' ? 'Reset' : 'Restablecer'}
            </button>
            
            <button
              onClick={handleRecalculateARV}
              disabled={recalculating || (recalcMode === 'user' && activeUserComps.length === 0)}
              className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid="recalculate-arv-btn"
            >
              {recalculating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              {lang === 'en' ? 'Recalculate ARV' : 'Recalcular ARV'}
            </button>
          </div>
        </div>
        
        {/* Mode descriptions */}
        <div className="mt-2 text-xs text-slate-500" data-testid="mode-description">
          {recalcMode === 'system' && (
            <span>{lang === 'en' ? 'Uses CHEKSTE auto-detected comparables only' : 'Usa solo comparables auto-detectados'}</span>
          )}
          {recalcMode === 'user' && (
            <span>{lang === 'en' ? 'Direct average of your added comparables (bypasses filters)' : 'Promedio directo de tus comparables (sin filtros)'}</span>
          )}
          {recalcMode === 'both' && (
            <span>{lang === 'en' ? 'Weighted combination of system and user comparables' : 'Combinación ponderada de comparables'}</span>
          )}
          {poolDelModo(recalcMode) > 0 && poolDelModo(recalcMode) < 3 && (
            <span className="text-amber-700" data-testid="thin-pool-note">
              {lang === 'en'
                ? ` — this will value the property on ${poolDelModo(recalcMode)} comparable${poolDelModo(recalcMode) === 1 ? '' : 's'}. With 3 or more, the estimate is firmer.`
                : ` — esto valorará la propiedad con ${poolDelModo(recalcMode)} comparable${poolDelModo(recalcMode) === 1 ? '' : 's'}. Con 3 o más, la estimación es más firme.`}
            </span>
          )}
        </div>
      </div>

      {/* Newly Added Comp Summary */}
      {newlyAddedComp && (
        <div className="p-4 border-b border-slate-200">
          <NewlyAddedCompCard 
            comp={newlyAddedComp} 
            onDismiss={() => setNewlyAddedComp(null)} 
          />
        </div>
      )}

      {/* User Comps Section */}
      <div className="border-b border-slate-200">
        <button
          onClick={() => setShowUserComps(!showUserComps)}
          className="w-full px-4 py-3 flex items-center justify-between bg-blue-50 hover:bg-blue-100 transition-colors"
          data-testid="user-comps-toggle"
        >
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-blue-800">
              {lang === 'en' ? 'User Added Comparables' : 'Comparables Agregados'}
            </span>
            <span className="px-2 py-0.5 bg-blue-200 text-blue-800 text-xs font-medium rounded-full">
              {userComps.length}
            </span>
          </div>
          {showUserComps ? <ChevronUp className="w-5 h-5 text-blue-600" /> : <ChevronDown className="w-5 h-5 text-blue-600" />}
        </button>
        
        {showUserComps && (
          <div className="p-4 space-y-3">
            {/* Add Comp Button/Form */}
            {!showAddComp ? (
              <button
                onClick={() => {
                  if (!canUseManualComps) {
                    if (onLockedFeature) {
                      onLockedFeature();
                    } else {
                      toast.error(lang === 'en' ? 'Upgrade to Investor to add manual comparables' : 'Actualiza a Investor para agregar comparables');
                    }
                    return;
                  }
                  setShowAddComp(true);
                }}
                className={`w-full p-3 border-2 border-dashed rounded-lg transition-colors flex items-center justify-center gap-2 ${
                  canUseManualComps 
                    ? 'border-blue-300 text-blue-600 hover:border-blue-500 hover:bg-blue-50' 
                    : 'border-slate-300 text-slate-400 hover:border-slate-400 hover:bg-slate-50'
                }`}
                data-testid="add-comp-btn"
              >
                {canUseManualComps ? <Plus className="w-5 h-5" /> : <Lock className="w-4 h-4" />}
                {lang === 'en' ? 'Add Comparable' : 'Agregar Comparable'}
                {!canUseManualComps && <span className="text-xs ml-1">(Investor)</span>}
              </button>
            ) : (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200" data-testid="add-comp-form">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-blue-800">
                    {lang === 'en' ? 'Add New Comparable' : 'Agregar Nuevo Comparable'}
                  </h4>
                  <button
                    onClick={() => setShowAddComp(false)}
                    className="p-1 text-slate-400 hover:text-slate-600 rounded"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="space-y-3">
                  {/* Address/URL Input */}
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">
                      {lang === 'en' ? 'Address or Listing URL *' : 'Dirección o URL *'}
                    </label>
                    {/* Suggests addresses as the user types (Mapbox, via our backend). Picking
                        one fills THIS field only; price, size and date stay manual. An
                        address Mapbox does not know goes through as typed. */}
                    <AddressAutocomplete
                      value={newCompInput}
                      onChange={setNewCompInput}
                      lang={lang}
                      placeholder={lang === 'en'
                        ? '123 Main St, Detroit, MI or Zillow/Realtor/Redfin URL'
                        : '123 Calle Principal, Detroit, MI o URL de Zillow/Realtor/Redfin'}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      inputProps={{ 'data-testid': 'comp-input-address' }}
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      {lang === 'en' 
                        ? 'Paste an address or link from Zillow, Realtor.com, or Redfin' 
                        : 'Pega una dirección o enlace de Zillow, Realtor.com o Redfin'}
                    </p>
                  </div>
                  
                  {/* Row 1: Price, Sqft, Sale Date */}
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        {lang === 'en' ? 'Sale Price' : 'Precio de Venta'}
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                        <input
                          type="text"
                          value={newCompPrice}
                          onChange={(e) => handlePriceChange(e.target.value, setNewCompPrice)}
                          placeholder="180,000"
                          className="w-full pl-7 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          data-testid="comp-input-price"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        {lang === 'en' ? 'Living Area (Sqft)' : 'Área (Pies²)'}
                      </label>
                      <input
                        type="text"
                        value={newCompSqft}
                        onChange={(e) => handlePriceChange(e.target.value, setNewCompSqft)}
                        placeholder="1,500"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        data-testid="comp-input-sqft"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        {lang === 'en' ? 'Sale Date' : 'Fecha de Venta'}
                      </label>
                      <input
                        type="date"
                        value={newCompSaleDate}
                        onChange={(e) => setNewCompSaleDate(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        data-testid="comp-input-date"
                      />
                    </div>
                  </div>
                  
                  {/* Row 2: Beds, Baths, Distance */}
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        {lang === 'en' ? 'Beds' : 'Habitaciones'}
                      </label>
                      <input
                        type="number"
                        value={newCompBeds}
                        onChange={(e) => setNewCompBeds(e.target.value)}
                        placeholder="3"
                        min="0"
                        max="20"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        data-testid="comp-input-beds"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        {lang === 'en' ? 'Baths' : 'Baños'}
                      </label>
                      <input
                        type="number"
                        value={newCompBaths}
                        onChange={(e) => setNewCompBaths(e.target.value)}
                        placeholder="2"
                        min="0"
                        max="20"
                        step="0.5"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        data-testid="comp-input-baths"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        {lang === 'en' ? 'Distance (mi)' : 'Distancia (mi)'}
                      </label>
                      <input
                        type="number"
                        value={newCompDistance}
                        onChange={(e) => setNewCompDistance(e.target.value)}
                        placeholder="0.5"
                        min="0"
                        max="50"
                        step="0.1"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        data-testid="comp-input-distance"
                      />
                    </div>
                  </div>
                  
                  {/* Row 3: Property Type, Condition */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        {lang === 'en' ? 'Property Type' : 'Tipo de Propiedad'}
                      </label>
                      <select
                        value={newCompPropertyType}
                        onChange={(e) => setNewCompPropertyType(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                        data-testid="comp-input-property-type"
                      >
                        <option value="">{lang === 'en' ? 'Auto-detect' : 'Auto-detectar'}</option>
                        <option value="single_family">{lang === 'en' ? 'Single Family' : 'Unifamiliar'}</option>
                        <option value="duplex">Duplex</option>
                        <option value="triplex">Triplex</option>
                        <option value="fourplex">Fourplex</option>
                        <option value="multi_family">{lang === 'en' ? 'Multi-Family' : 'Multifamiliar'}</option>
                        <option value="condo">Condo</option>
                        <option value="townhouse">Townhouse</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        {lang === 'en' ? 'Condition' : 'Condición'}
                      </label>
                      <select
                        value={newCompCondition}
                        onChange={(e) => setNewCompCondition(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                        data-testid="comp-input-condition"
                      >
                        <option value="">{lang === 'en' ? 'Auto-detect' : 'Auto-detectar'}</option>
                        <option value="renovated">{lang === 'en' ? 'Renovated' : 'Renovado'}</option>
                        <option value="updated">{lang === 'en' ? 'Updated' : 'Actualizado'}</option>
                        <option value="average">{lang === 'en' ? 'Average' : 'Promedio'}</option>
                        <option value="distressed">{lang === 'en' ? 'Distressed' : 'En Dificultades'}</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Data Source Toggle */}
                  <div className="mt-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-start gap-3">
                      <div className="pt-0.5">
                        <button
                          type="button"
                          onClick={() => setEnrichComp(!enrichComp)}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                            enrichComp ? 'bg-amber-500' : 'bg-slate-300'
                          }`}
                          data-testid="enrich-toggle"
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                              enrichComp ? 'translate-x-4' : 'translate-x-0.5'
                            }`}
                          />
                        </button>
                      </div>
                      <div className="flex-1">
                        <p className={`text-sm font-medium ${enrichComp ? 'text-amber-700' : 'text-slate-700'}`}>
                          {enrichComp 
                            ? (lang === 'en' ? 'Enhance with market data' : 'Mejorar con datos del mercado')
                            : (lang === 'en' ? 'Use only my data' : 'Usar solo mis datos')
                          }
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {enrichComp 
                            ? (lang === 'en' 
                              ? 'Fetches additional property details (may use credits)' 
                              : 'Obtiene detalles adicionales de la propiedad (puede usar créditos)')
                            : (lang === 'en' 
                              ? 'No external data used - only the details you provide' 
                              : 'Sin datos externos - solo los detalles que proporciones')
                          }
                        </p>
                      </div>
                    </div>
                    
                    {/* Warning when enrichment is enabled */}
                    {enrichComp && (
                      <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-amber-700">
                          {lang === 'en' 
                            ? 'This will use additional data and may count toward your usage limits.' 
                            : 'Esto usará datos adicionales y puede contar hacia tus límites de uso.'}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      onClick={() => setShowAddComp(false)}
                      className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                      {lang === 'en' ? 'Cancel' : 'Cancelar'}
                    </button>
                    <button
                      onClick={handleAddComp}
                      disabled={addingComp || !newCompInput.trim()}
                      className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
                      data-testid="add-comp-submit"
                    >
                      {addingComp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                      {lang === 'en' ? 'Add Comparable' : 'Agregar'}
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* User Comps List */}
            {userComps.length > 0 ? (
              <div className="space-y-2">
                {userComps.map((comp) => (
                  <CompCard 
                    key={comp.id} 
                    comp={comp} 
                    isUser={true}
                    onEdit={(c) => {
                      setEditingComp(c);
                      setEditForm({
                        sale_price: c.sale_price ? formatNumber(c.sale_price) : '',
                        sqft: c.sqft ? formatNumber(c.sqft) : '',
                        beds: c.beds || '',
                        baths: c.baths || '',
                        sold_date: c.sold_date?.split('T')[0] || '',
                        distance: c.distance || '',
                        condition_label: c.condition_label || '',
                        include_in_arv: c.include_in_arv !== false
                      });
                    }}
                    onDelete={handleDeleteComp}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-slate-500">
                <Home className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">
                  {lang === 'en' 
                    ? 'No user comparables added yet' 
                    : 'No hay comparables agregados aún'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* System Comps Section */}
      <div>
        <button
          onClick={() => setShowSystemComps(!showSystemComps)}
          className="w-full px-4 py-3 flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition-colors"
          data-testid="system-comps-toggle"
        >
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-slate-700">
              {lang === 'en' ? 'Sold Comparables' : 'Comparables Vendidos'}
            </span>
            <span className="px-2 py-0.5 bg-slate-200 text-slate-700 text-xs font-medium rounded-full">
              {qualifyingSystemComps.length}
            </span>
            {excludedCount > 0 && (
              <span className="text-xs text-slate-400" data-testid="comps-excluded-count">
                {/* When NOTHING qualified, "23 fetched, 23 excluded" beside "0 Sold Comps",
                    "4 Used in ARV" and an empty table is four numbers telling four stories.
                    Say the one thing that is true and complete. */}
                {qualifyingSystemComps.length === 0
                  ? (lang === 'en'
                      ? `${systemComps.length} comparables fetched, none usable`
                      : `${systemComps.length} comparables obtenidos, ninguno utilizable`)
                  : (lang === 'en'
                      ? `${systemComps.length} fetched, ${excludedCount} excluded`
                      : `${systemComps.length} obtenidos, ${excludedCount} excluidos`)}
              </span>
            )}
          </div>
          {showSystemComps ? <ChevronUp className="w-5 h-5 text-slate-600" /> : <ChevronDown className="w-5 h-5 text-slate-600" />}
        </button>
        
        {showSystemComps && (
          <div className="p-4 space-y-2">
            {qualifyingSystemComps.length > 0 ? (
              <>
                {/* ONLY the comps the ARV actually used are renderable.
                    There is deliberately no "show excluded" toggle: a non-qualifying comp
                    must never reach the screen, not even collapsed. The header still
                    reports how many were fetched and how many were dropped, as a count. */}
                <div data-testid="qualifying-comps-list">
                  {qualifyingSystemComps.map((comp, idx) => (
                    <CompCard key={`system-${idx}`} comp={comp} isUser={false} />
                  ))}
                </div>
              </>
            ) : systemComps.length > 0 && recalcMode === 'system' ? (
              // No qualifying sold comps, but the raw pool has listings/distressed only.
              <div className="text-center py-6 text-slate-500">
                <Info className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">
                  {lang === 'en'
                    ? `No sold, on-profile comparables — the ${systemComps.length} available are listings or distressed. Add your own renovated comps for an accurate ARV.`
                    : `No hay comparables vendidos en perfil — los ${systemComps.length} disponibles son listados o en dificultades. Agregue sus comps renovados.`}
                </p>
                {/* No "show them anyway" escape hatch. If nothing qualifies, the answer is
                    to add real comps, not to look at the ones we rejected. */}
              </div>
            ) : systemComps.length > 0 ? (
              // G: the user is on User Only / Blended — the system pool's shortcomings are
              // the system mode's story, not a message for a page valued from their comps.
              <div className="text-center py-6 text-slate-500" data-testid="system-pool-not-active">
                <Info className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">
                  {lang === 'en'
                    ? `${systemComps.length} system comparables fetched — none qualified. Switch to "System" to see why.`
                    : `${systemComps.length} comparables del sistema obtenidos — ninguno calificó. Cambia a "Sistema" para ver por qué.`}
                </p>
              </div>
            ) : (
              <div className="text-center py-6 text-slate-500">
                <Info className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">
                  {lang === 'en'
                    ? 'No system comparables available'
                    : 'No hay comparables del sistema disponibles'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit Modal - Enhanced with all fields */}
      {editingComp && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto" data-testid="edit-comp-modal">
            <div className="p-4 border-b border-slate-200 sticky top-0 bg-white">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-800">
                  {lang === 'en' ? 'Edit Comparable' : 'Editar Comparable'}
                </h3>
                <button
                  onClick={() => setEditingComp(null)}
                  className="p-1 text-slate-400 hover:text-slate-600 rounded"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-slate-500 mt-1 truncate">
                {editingComp.address}
              </p>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Sale Price */}
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">
                  {lang === 'en' ? 'Sale Price' : 'Precio de Venta'}
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                  <input
                    type="text"
                    value={editForm.sale_price || ''}
                    onChange={(e) => {
                      const formatted = formatInputValue(e.target.value);
                      setEditForm({...editForm, sale_price: formatted});
                    }}
                    className="w-full pl-7 pr-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              {/* Sqft */}
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">
                  {lang === 'en' ? 'Living Area (Sqft)' : 'Área (Pies²)'}
                </label>
                <input
                  type="text"
                  value={editForm.sqft || ''}
                  onChange={(e) => {
                    const formatted = formatInputValue(e.target.value);
                    setEditForm({...editForm, sqft: formatted});
                  }}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              {/* Beds / Baths */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">
                    {lang === 'en' ? 'Beds' : 'Habitaciones'}
                  </label>
                  <input
                    type="number"
                    value={editForm.beds || ''}
                    onChange={(e) => setEditForm({...editForm, beds: e.target.value})}
                    min="0"
                    max="20"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">
                    {lang === 'en' ? 'Baths' : 'Baños'}
                  </label>
                  <input
                    type="number"
                    value={editForm.baths || ''}
                    onChange={(e) => setEditForm({...editForm, baths: e.target.value})}
                    min="0"
                    max="20"
                    step="0.5"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              {/* Sale Date */}
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">
                  {lang === 'en' ? 'Sale Date' : 'Fecha de Venta'}
                </label>
                <input
                  type="date"
                  value={editForm.sold_date || ''}
                  onChange={(e) => setEditForm({...editForm, sold_date: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              {/* Distance */}
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">
                  {lang === 'en' ? 'Distance from Subject (miles)' : 'Distancia (millas)'}
                </label>
                <input
                  type="number"
                  value={editForm.distance || ''}
                  onChange={(e) => setEditForm({...editForm, distance: e.target.value})}
                  min="0"
                  max="50"
                  step="0.1"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              {/* Condition */}
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">
                  {lang === 'en' ? 'Property Condition' : 'Condición'}
                </label>
                <select
                  value={editForm.condition_label || ''}
                  onChange={(e) => setEditForm({...editForm, condition_label: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="">{lang === 'en' ? 'Not specified' : 'No especificado'}</option>
                  <option value="renovated">{lang === 'en' ? 'Renovated' : 'Renovado'}</option>
                  <option value="updated">{lang === 'en' ? 'Updated' : 'Actualizado'}</option>
                  <option value="average">{lang === 'en' ? 'Average' : 'Promedio'}</option>
                  <option value="distressed">{lang === 'en' ? 'Distressed' : 'En Dificultades'}</option>
                </select>
              </div>
              
              {/* Include in ARV */}
              <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                <input
                  type="checkbox"
                  id="includeInArv"
                  checked={editForm.include_in_arv !== false}
                  onChange={(e) => setEditForm({...editForm, include_in_arv: e.target.checked})}
                  className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                />
                <label htmlFor="includeInArv" className="text-sm text-slate-700">
                  {lang === 'en' ? 'Include in ARV calculation' : 'Incluir en cálculo de ARV'}
                </label>
              </div>
              
              <p className="text-xs text-slate-500 italic">
                {lang === 'en' 
                  ? 'Changes will recalculate the comp quality score immediately.'
                  : 'Los cambios recalcularán la puntuación de calidad inmediatamente.'}
              </p>
            </div>
            
            <div className="p-4 border-t border-slate-200 flex justify-end gap-2 sticky bottom-0 bg-white">
              <button
                onClick={() => setEditingComp(null)}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                {lang === 'en' ? 'Cancel' : 'Cancelar'}
              </button>
              <button
                onClick={handleUpdateComp}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                {lang === 'en' ? 'Save Changes' : 'Guardar Cambios'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComparableManager;
